// Lucka g7: när en etapp får nya datum ska daterade skyddsronder och möten kunna följa med.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarClock } from "lucide-react";
import {
  loadStageActivities,
  shiftActivities,
  shiftDate,
  type DatedActivity,
} from "@/lib/basProcess/stageDates";

interface Props {
  open: boolean;
  stageId: string | null;
  stageName: string;
  days: number;
  onClose: () => void;
  onDone?: () => void;
}

const fmt = (d: string) => new Date(d).toLocaleDateString("sv-SE");

export const StageDateShiftDialog: React.FC<Props> = ({
  open,
  stageId,
  stageName,
  days,
  onClose,
  onDone,
}) => {
  const [activities, setActivities] = useState<DatedActivity[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !stageId) return;
    loadStageActivities(stageId).then((rows) => {
      setActivities(rows);
      setSelected(Object.fromEntries(rows.map((r) => [r.id, true])));
    });
  }, [open, stageId]);

  const chosen = activities.filter((a) => selected[a.id]);

  const apply = async () => {
    setSaving(true);
    const moved = await shiftActivities(chosen, days);
    setSaving(false);
    toast.success(
      moved
        ? `${moved} aktiviteter flyttades ${days > 0 ? "+" : ""}${days} dagar`
        : "Inga aktiviteter flyttades"
    );
    onDone?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Flytta aktiviteter med etappen
          </DialogTitle>
          <DialogDescription>
            {stageName} flyttades {days > 0 ? "+" : ""}
            {days} dagar. Välj vilka daterade aktiviteter som ska flyttas lika mycket, så slipper du
            rätta dem en och en.
          </DialogDescription>
        </DialogHeader>

        {activities.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            Inga daterade skyddsronder eller samordningsmöten är kopplade till etappen.
          </p>
        ) : (
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {activities.map((a) => (
              <label
                key={a.id}
                className="flex items-center gap-3 rounded-md border p-2 text-sm"
              >
                <Checkbox
                  checked={!!selected[a.id]}
                  onCheckedChange={(v) =>
                    setSelected((prev) => ({ ...prev, [a.id]: v === true }))
                  }
                />
                <span className="min-w-0 flex-1 truncate">{a.label}</span>
                <span className="text-xs text-muted-foreground">
                  {fmt(a.currentDate)} → {fmt(shiftDate(a.currentDate, days))}
                </span>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Behåll datumen
          </Button>
          <Button onClick={apply} disabled={saving || chosen.length === 0}>
            {saving ? "Flyttar…" : `Flytta ${chosen.length} aktiviteter`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StageDateShiftDialog;
